/**
 * @fileoverview ProcessMappingService
 * Service for mapping order table data to required manufacturing processes
 * using the mapa_nombre_proceso_tabla collection
 */

import { pb } from '../helpers/pocketbase_helper';

/**
 * Service for mapping table data to manufacturing processes
 */
export class ProcessMappingService {
  static cache = new Map();
  static CACHE_KEY = 'process_mappings';
  static CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Maps internal relationships to their required processes
   * @param {Array} internalRelationships - Array of internal relationships with data
   * @returns {Promise<Array>} Mapped relationships with process names
   */
  static async mapTableDataToProcesses(internalRelationships) {
    if (!internalRelationships || internalRelationships.length === 0) {
      return [];
    }

    try {
      // Get process mappings from cache or database
      const mappings = await this.getProcessMappings();
      
      // Create a lookup map for faster access
      const mappingLookup = new Map();
      mappings.forEach(mapping => {
        mappingLookup.set(mapping.tabla, mapping.nombre);
      });

      // Map each relationship to its process
      const result = internalRelationships.map(relationship => {
        // Try exact match first
        let processName = mappingLookup.get(relationship.tableName);
        
        // If no exact match, try with parametros_ prefix
        if (!processName) {
          const parametrosKey = `parametros_${relationship.tableName}`;
          processName = mappingLookup.get(parametrosKey);
        }
        
        // If still no match, try reverse lookup - find any key that ends with the table name
        if (!processName) {
          for (const [key, value] of mappingLookup.entries()) {
            if (key.endsWith(`_${relationship.tableName}`) || key === relationship.tableName) {
              processName = value;
              break;
            }
          }
        }
        
        // Fallback mapping for common processes if no mapping found
        if (!processName) {
          const fallbackMappings = {
            'impresion': 'Proceso Impresión',
            'impresión': 'Proceso Impresión',
            'corte_conversion': 'Proceso Corte',
            'brillo_interno': 'Proceso Brillo',
            'pegue_interno': 'Proceso Pegue',
            'plastificado_interno': 'Proceso Plastificado',
            'troquelado_interno': 'Proceso Troquelado',
            'acabado_interno': 'Proceso Acabado',
            'recubrimiento': 'Proceso Recubrimiento'
          };
          
          processName = fallbackMappings[relationship.tableName];
        }
        
        return {
          ...relationship,
          processName
        };
      });
      
      return result;
    } catch (error) {
      throw new Error(`Error al mapear datos de tabla a procesos: ${error.message}`);
    }
  }

  /**
   * Gets all process mappings from the database
   * @returns {Promise<Array>} Array of process mappings
   */
  static async getProcessMappings() {
    // Check cache first
    const cached = this.getCachedMappings();
    if (cached) {
      return cached;
    }

    try {
      const result = await pb.collection('mapa_nombre_proceso_tabla').getList(1, 1000, {
        sort: 'tabla'
      });

      const mappings = result.items;
      
      // Cache the results
      this.setCachedMappings(mappings);
      
      return mappings;
    } catch (error) {
      throw new Error(`Error al obtener mapeos de procesos: ${error.message}`);
    }
  }

  /**
   * Gets required processes for given internal relationships
   * @param {Array} internalRelationships - Array of internal relationships
   * @returns {Promise<Array>} Array of unique process names
   */
  static async getRequiredProcesses(internalRelationships) {
    if (!internalRelationships || internalRelationships.length === 0) {
      return [];
    }

    try {
      const mappedRelationships = await this.mapTableDataToProcesses(internalRelationships);
      
      // Extract unique process names, filtering out null values
      const processNames = mappedRelationships
        .map(rel => rel.processName)
        .filter(name => name !== null && name !== undefined);
      
      // Remove duplicates
      const uniqueProcessNames = [...new Set(processNames)];
      return uniqueProcessNames;
    } catch (error) {
      throw new Error(`Error al obtener procesos requeridos: ${error.message}`);
    }
  }

  /**
   * Gets cached mappings if still valid
   * @returns {Array|null} Cached mappings or null if expired
   */
  static getCachedMappings() {
    const cached = this.cache.get(this.CACHE_KEY);
    if (!cached) {
      return null;
    }

    const now = Date.now();
    if (now - cached.timestamp > this.CACHE_DURATION) {
      this.cache.delete(this.CACHE_KEY);
      return null;
    }

    return cached.data;
  }

  /**
   * Sets cached mappings with timestamp
   * @param {Array} mappings - Mappings to cache
   */
  static setCachedMappings(mappings) {
    this.cache.set(this.CACHE_KEY, {
      data: mappings,
      timestamp: Date.now()
    });
  }

  /**
   * Clears the cache
   */
  static clearCache() {
    this.cache.clear();
  }

  /**
   * Gets process name for a specific table
   * @param {string} tableName - Name of the table
   * @returns {Promise<string|null>} Process name or null if not found
   */
  static async getProcessForTable(tableName) {
    if (!tableName) {
      return null;
    }

    try {
      const mappings = await this.getProcessMappings();
      const mapping = mappings.find(m => m.tabla === tableName);
      return mapping ? mapping.nombre : null;
    } catch (error) {
      throw new Error(`Error al obtener proceso para tabla ${tableName}: ${error.message}`);
    }
  }

  /**
   * Validates if all required processes are available
   * @param {Array} requiredProcesses - Array of required process names
   * @param {Array} availableProcesses - Array of available process names
   * @returns {Object} Validation result with missing processes
   */
  static validateProcessAvailability(requiredProcesses, availableProcesses) {
    if (!requiredProcesses || requiredProcesses.length === 0) {
      return {
        isValid: true,
        missingProcesses: [],
        coveragePercentage: 100
      };
    }

    const availableSet = new Set(availableProcesses || []);
    const missingProcesses = requiredProcesses.filter(process => !availableSet.has(process));
    
    const coveragePercentage = Math.round(
      ((requiredProcesses.length - missingProcesses.length) / requiredProcesses.length) * 100
    );

    return {
      isValid: missingProcesses.length === 0,
      missingProcesses,
      coveragePercentage
    };
  }
}
